import React, { useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import {
  BaseInputProps,
  ErrorsWrapper,
  useFormContext,
  useInputHelper
} from 'ui-builder';

import styles from '../AvatarInput.module.scss';
import { ALLOWED_TYPES, EDITOR_SIZE } from './constants';
import { resizeToSquare } from './resizeToSquare';
import { useZoomControl } from './useZoomControl';
import { ZoomControl } from './ZoomControl';
import { DropZone } from './DropZone';



interface EditAvatarInputWidgetProps extends BaseInputProps {
}

export function EditAvatarInputWidget(props: EditAvatarInputWidgetProps) {
  const [image, setImage] = useState<File | null>(null);
  const [isValid, setValid] = useState(true);

  const zoomControl = useZoomControl();

  const formContext = useFormContext();
  const editorRef = useRef<AvatarEditor>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const helper = useInputHelper(props);

  const setErrors = (errors: Record<string, string[]>) => {
    formContext.__setErrors!(errors);
  };

  const clearErrors = () => {
    setErrors({});
  };

  const processFile = (file: File) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors({ file: ['Please select a valid image file (PNG or JPEG).'] });
      setValid(false);
      setImage(null);
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const errors: string[] = [];

        if (img.width < 300) {
          errors.push('Please ensure that your image has a width of at least 300 pixels.');
        }
        if (img.height < 300) {
          errors.push('Please ensure that your image has a height of at least 300 pixels.');
        }

        if (errors.length > 0) {
          setErrors({ file: errors });
          setValid(false);
          setImage(null);
        } else {
          clearErrors();
          setValid(true);
          zoomControl.updateMaxScale(img.width, img.height);
          setImage(file);
        }
      };
      img.src = fileReader.result as string;
    };
    fileReader.readAsDataURL(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleImageChange = async () => {
    if (!editorRef.current || !isValid) return;

    const canvas = editorRef.current.getImage();

    const resized = await resizeToSquare(canvas);

    const img = new Image();
    img.onload = () => {
      // no actions
    };
    img.src = resized;

    helper.getOnChangeCallback()({
      target: { value: resized },
    });
  };

  const handleClear = () => {
    setImage(null);
    zoomControl.reset();
    clearErrors();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    helper.getOnChangeCallback()({
      target: { value: null },
    });
  };

  return (
    <ErrorsWrapper name={props.source}>
      <div className={styles.editorContainer}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png, image/jpeg"
          onChange={handleFileInputChange}
          className={styles.hiddenFileInput}
        />

        {!image && (
          <DropZone
            onFileSelect={processFile}
            onClick={handleClick}
          />
        )}

        {image && isValid && (
          <>
            <AvatarEditor
              ref={editorRef}
              image={image}
              width={EDITOR_SIZE}
              height={EDITOR_SIZE}
              border={0}
              borderRadius={EDITOR_SIZE / 2}
              color={[0, 0, 0, 0.6]}
              scale={zoomControl.scale}
              onImageChange={handleImageChange}
              onImageReady={handleImageChange}
            />

            <ZoomControl
              scale={zoomControl.scale}
              maxScale={zoomControl.maxScale}
              onScaleChange={zoomControl.setScale}
            />

            <button
              type="button"
              onClick={handleClear}
              className={styles.clearButton}
            >
              Clear
            </button>
          </>
        )}
      </div>
    </ErrorsWrapper>
  );
}
